/**
 * Final Calculation Card for Accountant Report View
 * Displays the 5-step final calculation breakdown
 */

import React from 'react';
import CalculationMethodology from '../CalculationMethodology';

interface FinalCalculation {
  step1_vp_loss?: number;
  step2_incremental?: number;
  additional_expenses?: number;
  total_before_threshold?: number;
  final_claim?: number;
  // Expense breakdown fields
  accountant_fees?: number;
  continuing_expenses?: number;
  extra_expenses?: number;
  threshold_amount?: number;
  threshold_pct?: number;
  threshold_type?: string;
}

interface FinalCalculationCardProps {
  finalCalculation: FinalCalculation;
  formatCurrency: (amount?: number) => string;
}

export default function FinalCalculationCard({
  finalCalculation,
  formatCurrency
}: FinalCalculationCardProps) {
  if (!finalCalculation) return null;

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">🧮</span>
          Final Calculation
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Step 1: VP Loss */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
            <p className="text-xs text-gray-600 font-medium mb-1">STEP 1</p>
            <p className="text-sm text-gray-700 mb-2 font-semibold">VP Loss</p>
            <p className="text-2xl font-bold text-gray-900 mb-2">
              {formatCurrency(finalCalculation.step1_vp_loss)}
            </p>
            <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1 font-mono">
              Benchmark − Coverage
            </div>
          </div>

          {/* Step 2: Incremental */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
            <p className="text-xs text-gray-600 font-medium mb-1">STEP 2</p>
            <p className="text-sm text-gray-700 mb-2 font-semibold">Incremental</p>
            <p className="text-2xl font-bold text-gray-900 mb-2">
              {formatCurrency(finalCalculation.step2_incremental)}
            </p>
            <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1">
              {(finalCalculation.step2_incremental || 0) < 0 ? 'Decline adj.' : 'Growth adj.'}
            </div>
          </div>

          {/* Step 3: Additional Expenses */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
            <p className="text-xs text-gray-600 font-medium mb-1">STEP 3</p>
            <p className="text-sm text-gray-700 mb-2 font-semibold">Add'l Expenses</p>
            <p className="text-2xl font-bold text-gray-900 mb-2">
              {formatCurrency(finalCalculation.additional_expenses)}
            </p>
            <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1">
              Extra costs
            </div>
          </div>

          {/* Step 4: Subtotal */}
          <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
            <p className="text-xs text-blue-700 font-medium mb-1">STEP 4: SUM</p>
            <p className="text-sm text-blue-800 mb-2 font-semibold">Subtotal</p>
            <p className="text-2xl font-bold text-blue-900 mb-2">
              {formatCurrency(finalCalculation.total_before_threshold)}
            </p>
            <div className="text-xs text-blue-700 bg-blue-100 rounded px-2 py-1 font-mono">
              {formatCurrency(finalCalculation.step1_vp_loss)} + {formatCurrency(finalCalculation.step2_incremental)}
            </div>
          </div>

          {/* Step 5: Final Claim */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 border-l-4 border-green-700">
            <p className="text-xs text-green-100 font-medium mb-1">FINAL RESULT</p>
            <p className="text-sm text-white mb-2 font-semibold">Claim Amount</p>
            <p className="text-2xl font-bold text-white mb-2">
              {formatCurrency(finalCalculation.final_claim)}
            </p>
            <div className="text-xs text-green-100 bg-green-700 bg-opacity-50 rounded px-2 py-1 font-mono">
              Subtotal − Threshold
            </div>
          </div>
        </div>
      </div>
      
      {/* Methodology Explanation */}
      <div className="mb-6">
        <CalculationMethodology 
          section="final_calculation" 
          data={{
            ...finalCalculation,
            accountant_fees: finalCalculation.accountant_fees ?? 0,
            continuing_expenses: finalCalculation.continuing_expenses ?? 0,
            extra_expenses: finalCalculation.extra_expenses ?? 0,
            threshold_amount: finalCalculation.threshold_amount ?? 0,
            threshold_pct: finalCalculation.threshold_pct ?? 0,
            threshold_type: finalCalculation.threshold_type ?? 'none'
          }} 
        />
      </div>
    </>
  );
}
